/**
 * Виробнича звітність за період — що здали пʼять цехів.
 *
 * Директору потрібна одна відповідь: за вчора (типово) — які вироби, скільки і
 * скільки ЧЗ на це пішло. Тому блок не залежить від зведення дашборда і тягне
 * власний запит: період тут перемикається, а решта картки оновлюється сама.
 *
 * Одиниці НЕ складаються між собою: меблі рахують у штуках, підлогу в м², і
 * «120» без одиниці було б неправдою. Через це підсумок цеху — список, а не
 * одне число.
 */
import { useMemo, useState } from 'react';
import { Modal, Segmented, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { productionApi } from '../../../api/production';
import { formatQty, formatVolume } from '../../../utils/formatters';
import {
  periodLabel,
  pluralReports,
  presetRange,
  shortDay,
  type ActivityPreset,
  type ActivityRange,
} from '../productionActivity';
import type {
  ProductionActivityBlank,
  ProductionActivityItem,
  ProductionActivityModule,
  ProductionActivityQuantity,
  ProductionModuleKey,
} from '../../../types/production';

/**
 * Заголовок картки — назва цеху без слова «Модуль»: пʼять карток поруч, і
 * повторене слово зʼїдає ширину, нічого не пояснюючи. Повна назва лишається
 * в шапці вікна деталей.
 */
const SHORT_LABELS: Record<ProductionModuleKey, string> = {
  prep: 'Заготівельний',
  format: 'Форматування',
  joinery: 'Столярний',
  chair: 'Стільцевий',
  packing: 'Пакування',
};

const PRESETS: { value: ActivityPreset; label: string }[] = [
  { value: 'yesterday', label: 'Вчора' },
  { value: 'today', label: 'Сьогодні' },
  { value: '7d', label: '7 днів' },
  { value: '30d', label: '30 днів' },
];

/** Скільки рядків показує картка цеху, доки її не розгорнули у вікно. */
const ITEMS_PREVIEW = 4;
const BLANKS_PREVIEW = 3;

/** «120 шт · 35,5 м²». Порожній список — прочерк, а не нуль без одиниці. */
function quantityLine(values: ProductionActivityQuantity[]): string {
  if (values.length === 0) return '—';
  return values.map((v) => formatQty(v.quantity, v.unit)).join(' · ');
}

/* ── Картка одного цеху ────────────────────────────────────────────────── */

function ModuleCard({
  data,
  onOpen,
}: {
  data: ProductionActivityModule;
  onOpen: (module: ProductionModuleKey) => void;
}) {
  const name = SHORT_LABELS[data.module] ?? data.label;
  const rest = data.items.length - ITEMS_PREVIEW;

  const head = (
    <div className="dash-pact-mod-head">
      <span className="dash-pact-mod-name">{name}</span>
      <span className="dash-pact-mod-reports">{pluralReports(data.reports_count)}</span>
    </div>
  );

  // Цех без звітів не має чого показати у вікні деталей, тому й не клікається:
  // порожня модалка дратує більше, ніж її відсутність.
  if (data.reports_count === 0) {
    return (
      <div className="dash-pact-mod is-empty">
        {head}
        <div className="dash-pact-none">без звітів</div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="dash-pact-mod"
      onClick={() => onOpen(data.module)}
      title={`${data.label}: показати деталі`}
    >
      {head}

      <div className="dash-pact-hero">
        {data.totals.length === 0 ? (
          <span className="dash-pact-hero-line">—</span>
        ) : (
          data.totals.map((t) => (
            <span key={t.unit} className="dash-pact-hero-line">
              {formatQty(t.quantity, t.unit)}
            </span>
          ))
        )}
      </div>

      <div className="dash-pact-items">
        {data.items.slice(0, ITEMS_PREVIEW).map((it) => (
          <div key={`${it.product_id}-${it.unit}`} className="dash-pact-row">
            <span className="dash-pact-row-name" title={it.product_name}>
              {it.product_name}
            </span>
            <span className="dash-pact-row-num">{formatQty(it.quantity, it.unit)}</span>
          </div>
        ))}
        {rest > 0 && <div className="dash-pact-more">ще {rest} позицій</div>}
      </div>

      {data.module === 'prep' && data.blanks.length > 0 && (
        <div className="dash-pact-blanks">
          <div className="dash-pact-blanks-head">Списано ЧЗ</div>
          {data.blanks.slice(0, BLANKS_PREVIEW).map((b) => (
            <div key={`${b.blank_id}-${b.grade ?? 'x'}`} className="dash-pact-row">
              <span className="dash-pact-row-name" title={`${b.blank_name} · ${b.grade_label}`}>
                {b.blank_name} · {b.grade_label}
              </span>
              <span className="dash-pact-row-num">{formatVolume(b.volume_m3)}</span>
            </div>
          ))}
          <div className="dash-pact-row dash-pact-total">
            <span className="dash-pact-row-name">Разом</span>
            <span className="dash-pact-row-num">{formatVolume(data.blanks_m3)}</span>
          </div>
        </div>
      )}
    </button>
  );
}

/* ── Вікно деталей цеху ────────────────────────────────────────────────── */

const ITEM_COLUMNS: ColumnsType<ProductionActivityItem> = [
  {
    title: 'Виріб',
    dataIndex: 'product_name',
    render: (_: unknown, it) => (
      <div>
        <div>{it.product_name}</div>
        <div className="dash-pact-cell-muted">{it.product_code}</div>
      </div>
    ),
  },
  {
    title: 'Замовлення',
    dataIndex: 'orders',
    width: 300,
    render: (_: unknown, it) =>
      it.orders.length === 0 ? (
        <span className="dash-pact-cell-muted">—</span>
      ) : (
        it.orders.map((o) => (
          <div key={o.id}>
            {o.external_number ? `№ ${o.external_number} ` : ''}
            <span className="dash-pact-cell-muted">{o.customer_name}</span>
          </div>
        ))
      ),
  },
  {
    title: 'Кількість',
    dataIndex: 'quantity',
    width: 140,
    align: 'right',
    render: (_: unknown, it) => (
      <span className="dash-pact-row-num">{formatQty(it.quantity, it.unit)}</span>
    ),
  },
];

const BLANK_COLUMNS: ColumnsType<ProductionActivityBlank> = [
  { title: 'Заготовка', dataIndex: 'blank_name' },
  { title: 'Гатунок', dataIndex: 'grade_label', width: 160 },
  {
    title: 'Обсяг',
    dataIndex: 'volume_m3',
    width: 140,
    align: 'right',
    render: (value: string) => <span className="dash-pact-row-num">{formatVolume(value)}</span>,
  },
];

function ModuleDetail({ data }: { data: ProductionActivityModule }) {
  return (
    <>
      <Table<ProductionActivityItem>
        size="small"
        columns={ITEM_COLUMNS}
        dataSource={data.items}
        rowKey={(it) => `${it.product_id}-${it.unit}`}
        pagination={false}
        locale={{ emptyText: 'Цех нічого не здав за цей період' }}
      />

      {data.module === 'prep' && data.blanks.length > 0 && (
        <div className="dash-pact-modal-blanks">
          <span className="dash-section-label">Списано ЧЗ</span>
          <Table<ProductionActivityBlank>
            size="small"
            columns={BLANK_COLUMNS}
            dataSource={data.blanks}
            rowKey={(b) => `${b.blank_id}-${b.grade ?? 'x'}`}
            pagination={false}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Разом</Table.Summary.Cell>
                <Table.Summary.Cell index={1}> </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <span className="dash-pact-row-num">{formatVolume(data.blanks_m3)}</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </div>
      )}

      <div className="dash-pact-modal-link">
        <Link to={`/production/${data.module}`}>Відкрити модуль →</Link>
      </div>
    </>
  );
}

/* ── Віджет ────────────────────────────────────────────────────────────── */

export function ProductionActivityWidget() {
  const [range, setRange] = useState<ActivityRange>(() => presetRange('yesterday'));
  const [openModule, setOpenModule] = useState<ProductionModuleKey | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['production', 'activity', range.from, range.to],
    queryFn: () => productionApi.activity(range.from, range.to),
    staleTime: 60_000,
  });

  const detail = useMemo(
    () => data?.modules.find((m) => m.module === openModule) ?? null,
    [data, openModule],
  );

  const period = periodLabel(range);
  const last = data?.last_report ?? null;

  const tiles = [
    {
      label: 'Звітів за період',
      value: data ? String(data.reports_count) : '—',
      sub: 'проведено звітів за зміну',
    },
    {
      label: 'Готової продукції',
      value: data ? quantityLine(data.finished_quantity) : '—',
      sub: 'дійшло до складу готової продукції',
    },
    {
      label: 'Списано ЧЗ',
      value: data ? formatVolume(data.blanks_total_m3) : '—',
      sub: 'чорнові заготовки, лише заготівельний модуль',
    },
  ];

  return (
    <div className="dash-card">
      <div className="dash-card-head">
        <div>
          <span className="dash-section-label">Виробнича звітність</span>
          <div className="dash-pact-period">
            {period}
            {last && (
              <>
                {' · останній звіт: '}
                {shortDay(last.date)}, {last.module_label} ({last.code})
              </>
            )}
          </div>
        </div>
        <Segmented<string>
          size="small"
          value={range.preset === 'custom' ? '' : range.preset}
          options={PRESETS}
          onChange={(value) => setRange(presetRange(value as ActivityPreset))}
        />
      </div>

      {isLoading ? (
        <div className="dash-pact-center">
          <Spin />
        </div>
      ) : isError || !data ? (
        <div className="dash-pact-error">Не вдалося завантажити виробничу звітність.</div>
      ) : (
        <>
          <div className="dash-pact-tiles">
            {tiles.map((t) => (
              <div key={t.label} className="dash-pact-tile">
                <div className="dash-pact-tile-label">{t.label}</div>
                <div className="dash-pact-tile-value">{t.value}</div>
                <div className="dash-pact-tile-sub">{t.sub}</div>
              </div>
            ))}
          </div>

          {data.reports_count === 0 ? (
            <div className="dash-pact-center">
              <div className="dash-pact-none">За цей період звітів не було.</div>
              {last && (
                <button
                  type="button"
                  className="dash-link dash-pact-jump"
                  onClick={() => setRange({ preset: 'custom', from: last.date, to: last.date })}
                >
                  Показати {shortDay(last.date)}
                </button>
              )}
            </div>
          ) : (
            <div className="dash-pact-grid">
              {data.modules.map((m) => (
                <ModuleCard key={m.module} data={m} onOpen={setOpenModule} />
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        open={detail !== null}
        onCancel={() => setOpenModule(null)}
        footer={null}
        width={900}
        destroyOnClose
        title={detail ? `${detail.label} · ${period}` : ''}
      >
        {detail && <ModuleDetail data={detail} />}
      </Modal>
    </div>
  );
}
